import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AddProjectPage from './pages/AddProjectPage';
import ReportGenerationPage from './pages/ReportGenerationPage';
import FeedbackPage from './pages/FeedbackPage';
import CommunicationPage from './pages/CommunicationPage';
import WordPressProjectsPage from './pages/WordPressProjectsPage';
import ProfilePage from './pages/ProfilePage';
import CitizenDashboardPage from './pages/CitizenDashboardPage';
import MailSettingsPage from './pages/MailSettingsPage';
import UsersPage from './pages/UsersPage';

// Layout
import MainLayout from './components/MainLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#003c8f', // deep county blue
    },
    secondary: {
      main: '#ffb300', // golden accent
    },
    background: {
      default: '#f4f6fb',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

const LoadingScreen = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

// Public WordPress projects route that shows MainLayout when a user is logged in
const WordPressProjectsWrapper: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <MainLayout>
        <WordPressProjectsPage />
      </MainLayout>
    );
  }

  return <WordPressProjectsPage />;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                {/* Public citizen landing dashboard */}
                <Route path="/" element={<CitizenDashboardPage />} />
                <Route path="/wordpress-projects" element={<WordPressProjectsWrapper />} />
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/*"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <Routes>
                          {/* Default after login: internal dashboard */}
                          <Route path="/dashboard" element={<DashboardPage />} />
                          <Route path="/projects/add" element={<AddProjectPage />} />
                          <Route path="/reports" element={<ReportGenerationPage />} />
                          <Route path="/feedback" element={<FeedbackPage />} />
                          <Route path="/communication" element={<CommunicationPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route path="/settings/mail" element={<MailSettingsPage />} />
                          <Route path="/users" element={<UsersPage />} />
                          {/* More routes will be added here */}
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Suspense>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
